/**
 * Firebase Auth compatibility shim — replaces the Supabase client.
 * Components that call supabase.auth.getUser() / signOut() / onAuthStateChange()
 * continue to work unchanged. The supabase.from() API is not implemented
 * (all data operations now use src/lib/api.ts instead).
 */
import { auth, signOut, onAuthStateChanged } from './firebase';

function mapFirebaseUser(user: import('firebase/auth').User | null) {
  if (!user) return null;
  return {
    id:    user.uid,
    email: user.email ?? '',
    user_metadata: { full_name: user.displayName ?? '' },
  };
}

export const supabase = {
  auth: {
    getUser: async () => {
      const user = auth.currentUser;
      return { data: { user: mapFirebaseUser(user) }, error: null };
    },

    getSession: async () => {
      const user = auth.currentUser;
      if (!user) return { data: { session: null }, error: null };
      const token = await user.getIdToken();
      return {
        data: {
          session: {
            access_token: token,
            user: mapFirebaseUser(user),
            expires_at: Date.now() / 1000 + 3600,
          },
        },
        error: null,
      };
    },

    signOut: async () => {
      await signOut(auth);
      return { error: null };
    },

    onAuthStateChange: (
      callback: (event: string, session: any) => void,
    ) => {
      const unsub = onAuthStateChanged(auth, user => {
        const session = user
          ? { access_token: '', user: mapFirebaseUser(user) }
          : null;
        callback(user ? 'SIGNED_IN' : 'SIGNED_OUT', session);
      });
      return { data: { subscription: { unsubscribe: unsub } } };
    },
  },

  // Stub — not used; data fetching goes through src/lib/api.ts
  channel: (_name: string) => ({
    on:        (_event: any, _opts: any, _cb: any) => ({ subscribe: () => {} }),
    subscribe: () => ({ unsubscribe: () => {} }),
    unsubscribe: () => {},
  }),
};

// Legacy helpers (referenced in Boards.tsx)
export const getCurrentSession = async () => {
  const user = auth.currentUser;
  if (!user) return null;
  return { user: mapFirebaseUser(user) };
};

export const isAuthenticated = async () => !!auth.currentUser;
